import React, { useEffect, useState } from 'react';
import {
  type SidePanelProvider,
  type EditorPlugin,
  type AhaEditor,
} from '@aha-app/aha-editor-v2';
import { ApplicationView } from '@aha-app/mvc';
import { SidePanel } from '@aha-app/whiteboard/dist/SidePanel';
import {
  type SidePanelItem,
  type ISidePanelController,
  type SidePanelProps,
} from 'javascripts/shared-types';

interface SidePanelPluginOptions {
  objectType: SidePanelProps['documentType'];
}

export const SidePanelPluginFactory = (
  options: SidePanelPluginOptions
): EditorPlugin => {
  let sidePanelController: ISidePanelController;
  let ahaEditor: AhaEditor;
  let _setSidePanels: (sidePanels: SidePanelItem[]) => void | undefined;
  let _setOpenSidePanel: (panel: SidePanelItem['id']) => void | undefined;
  let resolveSidePanelP: () => void;
  const sidePanelP = new Promise<void>(resolve => {
    resolveSidePanelP = resolve;
  });

  const setSidePanelController = _sidePanelController => {
    sidePanelController = _sidePanelController;
  };

  const SidepanelComponnent: React.FC = () => {
    const { objectType } = options;
    const fullScreenProvider = ahaEditor.getProvider('fullScreen');
    const [sidePanels, setSidePanels] = useState<SidePanelItem[]>();
    const [openSidePanel, setOpenSidePanel] = useState<
      SidePanelItem['id'] | null
    >(null);

    useEffect(() => {
      _setSidePanels = setSidePanels;
      _setOpenSidePanel = setOpenSidePanel;
      resolveSidePanelP();
    }, [setSidePanels, setOpenSidePanel]);

    useEffect(() => {
      if (openSidePanel && sidePanelController) {
        sidePanelController.actionOpenSidePanel(openSidePanel);
        setOpenSidePanel(null);
      }
    }, [openSidePanel]);

    if (!sidePanels || sidePanels.length === 0) {
      return null;
    }

    return (
      <SidePanel
        controllerRef={setSidePanelController}
        className='aha-editor-field-v2__sidepanel'
        documentType={objectType}
        sidePanelItems={sidePanels}
        hidden={!fullScreenProvider?.isFullScreen}
      />
    );
  };

  const provider: SidePanelProvider = {
    type: 'sidePanel',

    onSetHidden(hidden: boolean) {
      if (sidePanelController) {
        sidePanelController.actionSetHidden(hidden);
      }
    },

    setSidePanels(sidePanels: SidePanelItem[]) {
      // Queue the sidepanel update until the component is ready.
      void sidePanelP.then(() => {
        _setSidePanels(sidePanels);
      });
    },

    onOpenSidePanel(panel: SidePanelItem['id']) {
      const fullScreenProvider = ahaEditor.getProvider('fullScreen');
      fullScreenProvider?.onSetFullScreen?.(true);

      void sidePanelP.then(() => {
        _setOpenSidePanel(panel);
      });
    },

    onCloseSidePanel() {
      sidePanelController.actionCloseSidePanel();
    },
  };

  return {
    name: 'Sidepanel plugin',
    provider,
    handlers: {
      onInit(_ahaEditor) {
        ahaEditor = _ahaEditor;
      },
    },
    layoutComponent: {
      location: 'right',
      Component: ApplicationView(SidepanelComponnent),
    },
  };
};
